import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiLockClosed, HiEye, HiEyeSlash, HiCheckCircle, HiShieldCheck } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { changePassword } from '../../services/settingsService';
import { toast } from '../../utils/toast';

export default function PasswordSettings() {
  const { currentUser } = useAuth();
  const [changing, setChanging] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  // Password strength validation
  const getPasswordStrength = password => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return { text: 'Weak', color: 'text-red-600', bg: 'bg-red-600' };
    if (passwordStrength <= 3)
      return { text: 'Medium', color: 'text-orange-600', bg: 'bg-orange-600' };
    return { text: 'Strong', color: 'text-green-600', bg: 'bg-green-600' };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setChanging(true);
      await changePassword({
        uid: currentUser.uid,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: 'Incorrect password' });
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.message || 'Failed to change password');
      }
    } finally {
      setChanging(false);
    }
  };

  const togglePasswordVisibility = field => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const strengthLabel = getStrengthLabel();

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/30">
            <HiLockClosed className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-black">Password Settings</h2>
            <p className="text-sm text-gray-600">
              Update your password to keep your account secure
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4"
        >
          <div className="flex gap-3">
            <HiShieldCheck className="h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">Password Requirements</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-blue-800">
                <li>At least 8 characters long</li>
                <li>Mix of uppercase and lowercase letters</li>
                <li>Include numbers and special characters</li>
                <li>Different from your current password</li>
              </ul>
            </div>
          </div>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Password */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-black">
              Current Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <HiLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={e => {
                  setFormData({ ...formData, currentPassword: e.target.value });
                  setErrors({ ...errors, currentPassword: '' });
                }}
                className={`w-full rounded-lg border-2 py-3 pl-10 pr-12 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                  errors.currentPassword
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                    : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                }`}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.current ? (
                  <HiEyeSlash className="h-5 w-5" />
                ) : (
                  <HiEye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-2 text-xs text-red-600">{errors.currentPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-black">
              New Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <HiLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={e => {
                  setFormData({ ...formData, newPassword: e.target.value });
                  setErrors({ ...errors, newPassword: '' });
                }}
                className={`w-full rounded-lg border-2 py-3 pl-10 pr-12 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                  errors.newPassword
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                    : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                }`}
                placeholder="Enter your new password"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? (
                  <HiEyeSlash className="h-5 w-5" />
                ) : (
                  <HiEye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-2 text-xs text-red-600">{errors.newPassword}</p>
            )}

            {/* Password Strength Indicator */}
            <AnimatePresence>
              {formData.newPassword && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-3 space-y-2"
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Password Strength:</span>
                    <span className={`font-semibold ${strengthLabel.color}`}>
                      {strengthLabel.text}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(level => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength ? strengthLabel.bg : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-black">
              Confirm New Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <HiLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={e => {
                  setFormData({ ...formData, confirmPassword: e.target.value });
                  setErrors({ ...errors, confirmPassword: '' });
                }}
                className={`w-full rounded-lg border-2 py-3 pl-10 pr-12 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                  errors.confirmPassword
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                    : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                }`}
                placeholder="Confirm your new password"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? (
                  <HiEyeSlash className="h-5 w-5" />
                ) : (
                  <HiEye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 text-xs text-red-600">{errors.confirmPassword}</p>
            )}
            {!errors.confirmPassword &&
              formData.confirmPassword &&
              formData.newPassword === formData.confirmPassword && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2 flex items-center gap-1 text-xs text-green-600"
                >
                  <HiCheckCircle className="h-4 w-4" />
                  Passwords match
                </motion.p>
              )}
          </div>

          {/* Action Button */}
          <div className="border-t border-gray-200 pt-6">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={changing}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 disabled:opacity-50"
            >
              {changing ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                  />
                  Changing Password...
                </>
              ) : (
                <>
                  <HiShieldCheck className="h-5 w-5" />
                  Change Password
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
}
